/**
 * Game Management API Client for Frontend
 * Handles all communication with the Game Management backend
 */

import { API_BASE_URL } from '../../config/api';

class GameManagementApiClient {
    constructor(baseUrl = API_BASE_URL) {
        this.baseUrl = baseUrl;
    }

    /**
     * Get all available game types
     * @returns {Promise<Object>} Game types list
     */
    async getGameTypes() {
        const response = await fetch(`${this.baseUrl}/games/types`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to get game types');
        }

        return response.json();
    }

    /**
     * Get detailed configuration for a specific game type
     * @param {string} gameTypeId - Game type ID
     * @returns {Promise<Object>} Game type details
     */
    async getGameTypeDetails(gameTypeId) {
        const response = await fetch(`${this.baseUrl}/games/types/${gameTypeId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to get game type details');
        }

        return response.json();
    }

    /**
     * Get preset scenario details (for chat games)
     * @param {string} gameTypeId - Game type ID
     * @param {string} scenarioId - Scenario ID
     * @returns {Promise<Object>} Scenario details
     */
    async getPresetScenario(gameTypeId, scenarioId) {
        const response = await fetch(`${this.baseUrl}/games/types/${gameTypeId}/scenarios/${scenarioId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to get preset scenario');
        }

        return response.json();
    }

    /**
     * Create a new game
     * @param {Object} params - Game creation parameters
     * @param {string} params.game_type - Type of game
     * @param {string} params.game_name - Name of the game
     * @param {Object} params.config - Game configuration
     * @param {Array} params.players - Player configurations
     * @returns {Promise<Object>} Game creation result
     */
    async createGame(params) {
        const response = await fetch(`${this.baseUrl}/games/create`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(params)
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to create game');
        }

        return response.json();
    }

    /**
     * List all games with optional filters
     * @param {Object} filters - Filter parameters
     * @param {string} filters.game_type - Filter by game type
     * @param {string} filters.status - Filter by status
     * @param {number} filters.limit - Limit results
     * @returns {Promise<Object>} Games list
     */
    async listGames(filters = {}) {
        const url = new URL(`${this.baseUrl}/games/list`);

        if (filters.game_type) {
            url.searchParams.append('game_type', filters.game_type);
        }
        if (filters.status) {
            url.searchParams.append('status', filters.status);
        }
        if (filters.limit) {
            url.searchParams.append('limit', filters.limit);
        }
        // 🔒 Admin-only route since 09-07 (audit C1). Key rides as a query
        // param, not X-Admin-Key: the custom header trips the CORS preflight
        // (same constraint AdminPage adminFetch documents). Shares AdminPage's
        // stored key ('adminApiKey'); without it the backend 401s.
        const adminKey = localStorage.getItem('adminApiKey');
        if (adminKey) url.searchParams.append('admin_key', adminKey);

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to list games (admin key required — set it on the Admin page)');
        }

        return response.json();
    }

    /**
     * Get detailed status of a specific game
     * @param {string} gameId - Game ID
     * @returns {Promise<Object>} Game status
     */
    async getGameStatus(gameId) {
        // 🔒 Admin-only route since 09-07 (audit C1 rider: this management read
        // returns full seat data, seat secrets included). Query-param key, same
        // CORS constraint as listGames above.
        const url = new URL(`${this.baseUrl}/games/${gameId}/status`);
        const adminKey = localStorage.getItem('adminApiKey');
        if (adminKey) url.searchParams.append('admin_key', adminKey);
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to get game status (admin key required — set it on the Admin page)');
        }

        return response.json();
    }

    /**
     * Join a game as a human player
     * @param {string} gameId - Game ID
     * @param {string} playerName - Player name to join as
     * @returns {Promise<Object>} Join result with auth token
     */
    async joinGame(gameId, playerName) {
        const response = await fetch(`${this.baseUrl}/games/${gameId}/join`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ player_name: playerName })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to join game');
        }

        return response.json();
    }

    /**
     * Get available AI models
     * @returns {Promise<Object>} AI models list
     */
    async getAvailableModels() {
        const response = await fetch(`${this.baseUrl}/games/models`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to get available models');
        }

        return response.json();
    }

    /**
     * Get the configuration from the most recently created game
     * @returns {Promise<Object>} Latest game configuration
     */
    async getLatestGameConfig() {
        const response = await fetch(`${this.baseUrl}/games/latest-config`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to get latest game config');
        }

        return response.json();
    }
}

// Export a singleton instance
const gameManagementApi = new GameManagementApiClient();
export default gameManagementApi;
